import { ViewHook } from 'phoenix_live_view';

import { buildPropsHelper, PropsHelper } from './helpers/props';

type NavigableSearchHookProps = {
  searchInputId: string;
};

type NavigableSearchHook = Partial<ViewHook> &
  PropsHelper<NavigableSearchHookProps> & {
    el: HTMLElement;
    props: NavigableSearchHookProps;
    searchInput: HTMLInputElement | null;

    initialize: () => void;
    addEventListeners: () => void;
    removeEventListeners: () => void;
    onKeyDown: (e: KeyboardEvent) => void;
  };

export default {
  NavigableSearch: {
    ...buildPropsHelper(),

    mounted() {
      this.onKeyDownCallback = this.onKeyDown.bind(this);

      this.initialize();
    },

    updated() {
      if (this.propsChanged()) {
        this.removeEventListeners();
        this.initialize();
      }
    },

    destroyed() {
      this.removeEventListeners();
    },

    initialize() {
      this.setProps();

      this.searchInput = document.getElementById(this.searchInputId) as HTMLInputElement;

      this.addEventListeners();
    },

    getProps(): NavigableSearchHookProps {
      return {
        searchInputId: this.el.dataset.searchInputId || '',
      };
    },

    addEventListeners() {
      if (this.searchInput) {
        this.searchInput.addEventListener('keydown', this.onKeyDownCallback);
      }
    },

    removeEventListeners() {
      if (this.searchInput) {
        this.searchInput.removeEventListener('keydown', this.onKeyDownCallback);
      }
    },

    onKeyDown(e: KeyboardEvent) {
      const searchResults = this.el.querySelectorAll('[data-search-result]') as NodeListOf<HTMLElement>;
      let currentSearchResult = this.el.querySelector('[data-search-result].active') as HTMLElement | null;
      const currentIndex = currentSearchResult ? Array.from(searchResults).indexOf(currentSearchResult) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();

        if (currentIndex < searchResults.length - 1) {
          searchResults[currentIndex + 1]?.classList.add('active');
          currentSearchResult?.classList.remove('active');
        }
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();

        if (currentIndex > 0) {
          searchResults[currentIndex - 1]?.classList.add('active');
          currentSearchResult?.classList.remove('active');
        } else {
          currentSearchResult?.classList.remove('active');
        }
      }

      if (e.key === 'Enter') {
        e.preventDefault();

        if (currentSearchResult) {
          const actionButton = currentSearchResult.querySelector(
            '[data-search-result-action]',
          ) as HTMLButtonElement | null;

          actionButton?.click();
        }
      }

      currentSearchResult = this.el.querySelector('[data-search-result].active') as HTMLElement | null;

      currentSearchResult?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    },
  } as NavigableSearchHook,
};
